import { Switch } from '@headlessui/react';
import { type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { toggleColorClasses } from '../lib/colorSchemes';
import { cn } from '../lib/utils';

type Color = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';
type Size = 'sm' | 'md' | 'lg';

// ── Variants ──────────────────────────────────────────────────────────────────

const toggleVariants = tv({
  slots: {
    track: [
      // 'group' lets thumb use group-data-[checked]: to detect checked state
      'group relative inline-flex shrink-0 cursor-pointer items-center rounded-full',
      'bg-uniform-gray-200 dark:bg-uniform-gray-700',
      'transition-colors duration-200',
      '[outline:none] focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50'
    ],
    thumb: [
      'pointer-events-none inline-block rounded-full bg-white shadow-sm',
      'transition-transform duration-200'
    ]
  },
  variants: {
    size: {
      sm: {
        // track 36×20px, thumb 16×16px → off: 2px margin, on: 36-16-2=18px
        track: 'h-5 w-9',
        thumb: 'h-4 w-4 translate-x-0.5 group-data-[checked]:translate-x-[18px]'
      },
      md: {
        // track 44×24px, thumb 16×16px → off: 4px, on: 44-16-4=24px
        track: 'h-6 w-11',
        thumb: 'h-4 w-4 translate-x-1 group-data-[checked]:translate-x-6'
      },
      lg: {
        // track 56×28px, thumb 20×20px → off: 4px, on: 56-20-4=32px
        track: 'h-7 w-14',
        thumb: 'h-5 w-5 translate-x-1 group-data-[checked]:translate-x-8'
      }
    },
    color: toggleColorClasses
  },
  defaultVariants: {
    size: 'md',
    color: 'secondary'
  }
});

type ToggleVariants = VariantProps<typeof toggleVariants>;

// ── Component ─────────────────────────────────────────────────────────────────

interface ToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  color?: Color;
  size?: Size;
  disabled?: boolean;
  /** Optional label rendered next to the toggle; clicking it also toggles */
  children?: ReactNode;
  className?: string;
  /** Accessible name for the switch, which the visual label is not wired to */
  'aria-label'?: string;
}

function Toggle({
  checked,
  onChange,
  color = 'secondary',
  size = 'md',
  disabled,
  children,
  className,
  'aria-label': ariaLabel
}: ToggleProps) {
  const { track, thumb } = toggleVariants({ color, size });

  const switchEl = (
    <Switch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(track(), className)}>
      <span className={thumb()} />
    </Switch>
  );

  if (!children) return switchEl;

  return (
    <div className="inline-flex items-center gap-2">
      {switchEl}
      <span
        className="cursor-pointer select-none text-sm font-medium text-uniform-gray-900 dark:text-uniform-gray-100"
        onClick={() => !disabled && onChange?.(!checked)}>
        {children}
      </span>
    </div>
  );
}

export { Toggle, toggleVariants };
export type { ToggleProps, ToggleVariants };
